import * as React from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { Link, graphql, useStaticQuery } from 'gatsby';
import clsx from 'clsx';
import { icon, library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from '@reach/router';
import useMediaQuery from '../../hooks/useMediaQuery'; // Ensure this path is correct
import { extractLanguage, getStoryblokAlt } from '../../helpers/common';
import * as icons from '../../icons/icon-pack';
import Container from '../Container';
import ReferencesTileImage from './references-tile-image';

function getStoryblokDimensions(filename) {
  const [width, height] = filename?.split('/')[5]?.split('x') || [];
  return { width, height };
}

function getStoryblokImageUrl(filename, width = 800, format = 'webp') {
  if (!filename || filename.includes('.svg') || !filename.includes('a.storyblok.com')) {
    return filename;
  }

  return `${filename}/m/${width}x0/filters:format(${format})`;
}

function getStoryblokImageSrcSet(filename, widths, format = 'webp') {
  if (!filename || filename.includes('.svg') || !filename.includes('a.storyblok.com')) {
    return undefined;
  }

  return widths.map((width) => `${getStoryblokImageUrl(filename, width, format)} ${width}w`).join(', ');
}

export default function ReferencesTiles({ blok }) {
  library.add({ ...icons });

  const location = useLocation();
  const currentLanguage = extractLanguage(location.pathname);

  const tiles = Array.isArray(blok.referenceTile) ? blok.referenceTile : [];

  if (!tiles.length && !location.pathname.includes('/references')) return null;

  const getLink = (tile) => {
    if (currentLanguage === 'en' && tile.link?.story?.translated_slugs?.[0]?.path) {
      return `/en/${tile.link.story.translated_slugs[0].path}`;
    }
    return `/${tile.link?.cached_url || ''}`;
  };


  if(blok.tile.length > 0 && location.pathname.includes('/references')) {
    return <ReferencesTilesWithTiles blok={blok} />;
  }

  return (
    <div {...storyblokEditable(blok)} className="py-6">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tiles.map((tile) => {
            const linkUrl = getLink(tile);
            const label = tile.link_text || tile.link?.story?.name || '';
            return (
              <div
                key={tile._uid}
                {...storyblokEditable(tile)}
                className="flex flex-col"
              >
                <Link
                  to={linkUrl}
                  title={tile.link?.link_title || undefined}
                  aria-label={label}
                  className="block relative overflow-hidden aspect-[3/2]"
                >
                  {tile.image?.filename && (
                    (() => {
                      const { width, height } = getStoryblokDimensions(tile.image.filename);
                      return (
                        <picture>
                          <source
                            type="image/avif"
                            srcSet={getStoryblokImageSrcSet(tile.image.filename, [400, 600, 800, 1000], 'avif')}
                            sizes="(max-width: 768px) 100vw, 50vw"
                          />
                          <img
                            src={getStoryblokImageUrl(tile.image.filename, 800)}
                            srcSet={getStoryblokImageSrcSet(tile.image.filename, [400, 600, 800, 1000])}
                            sizes="(max-width: 768px) 100vw, 50vw"
                            alt={getStoryblokAlt(tile.image, '')}
                            width={width}
                            height={height}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        </picture>
                      );
                    })()
                  )}
                  <div className="absolute inset-0 bg-black/50 flex items-end justify-center pb-5">
                    {tile.logo?.filename ? (
                      (() => {
                        const { width, height } = getStoryblokDimensions(tile.logo.filename);
                        return (
                          <img
                            src={tile.logo.filename}
                            alt={getStoryblokAlt(tile.logo, tile.link?.story?.name || '')}
                            width={width || 175}
                            height={height || 64}
                            className="max-h-14 max-w-[55%] object-contain "
                            loading="lazy"
                            decoding="async"
                          />
                        );
                      })()
                    ) : (
                      <span className="text-white text-xl font-bold text-center px-4">
                        {tile.link?.story?.name}
                      </span>
                    )}
                  </div>
                </Link>

                <div className="pt-4 flex flex-col flex-1">
                  {Array.isArray(tile.badges) && tile.badges.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-3">
                      {tile.badges.map((badge) => (
                        <span
                          key={badge._uid}
                          className="text-xs border bg-[#e5e5e5] px-3 py-1 text-[#050505] font-semibold"
                        >
                          {badge.badge}
                        </span>
                      ))}
                    </div>
                  )}

                  {tile.description && (
                    <p className="text-sm mb-3 leading-relaxed">
                      {tile.description}
                    </p>
                  )}

                  {tile.link_text && (
                    <Link
                      to={linkUrl}
                      title={tile.link?.link_title || undefined}
                      aria-label={tile.link_text}
                      className="text-[19px] text-latori-pink hover:opacity-75 transition-opacity mt-auto font-bold"
                    >
                      {tile.link_text}
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {blok.cta_text && (
          <div className="flex justify-center mt-10">
            <Link
              to={currentLanguage === 'en' ? '/en/references' : '/references'}
              className="button-primary"
            >
              {blok.cta_text}
            </Link>
          </div>
        )}
      </Container>
    </div>
  );
}

function ReferencesTilesWithTiles({ blok }) {
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const currentLanguage = extractLanguage(location.pathname);
  const { component } = blok;

  const tileIds = React.useMemo(() => (Array.isArray(blok.tile) ? blok.tile : []), [blok.tile]);

  // Tiles are resolved at build time so their links are in the static HTML (crawlers and
  // internal-link audits only see those). 'references-tiles.tile' can't go in
  // resolveRelations because Storyblok resolves at most 50 relations per story.
  const { allStoryblokEntry } = useStaticQuery(graphql`
    query ReferenceTileEntries {
      allStoryblokEntry(filter: { field_component: { eq: "reference" } }) {
        nodes {
          uuid
          lang
          name
          full_slug
          referenceTileContent
        }
      }
    }
  `);

  const buildTimeStories = React.useMemo(() => tileIds.map((id) => {
    const node = allStoryblokEntry.nodes.find((n) => n.uuid === id && n.lang === currentLanguage)
      || allStoryblokEntry.nodes.find((n) => n.uuid === id);
    if (!node) return null;
    return {
      uuid: node.uuid,
      name: node.name,
      href: `/${node.full_slug}`,
      content: JSON.parse(node.referenceTileContent || '{}'),
    };
  }).filter(Boolean), [tileIds, allStoryblokEntry, currentLanguage]);

  // 1. Store the FULL list of stories here.
  const [allStories, setAllStories] = React.useState(buildTimeStories);

  const tileIdsJoin = React.useMemo(() => tileIds.join(','), [tileIds]);

  // Only fetch client-side when tiles are missing from the build data, e.g. tiles just
  // added in the Storyblok editor preview.
  React.useEffect(() => {
    if (!tileIds.length || buildTimeStories.length === tileIds.length) return;
    let isMounted = true;
    import('storyblok-js-client')
      .then(({ default: StoryblokClient }) => {
        const Storyblok = new StoryblokClient({
          accessToken: process.env.GATSBY_STORYBLOK_ACCESS_TOKEN,
        });
        return Storyblok.get('cdn/stories', {
          by_uuids: tileIds.join(','),
          version: 'published',
          per_page: tileIds.length,
        });
      })
      .then((res) => {
        if (isMounted) {
          const { stories } = res.data;
          const orderedStories = tileIds
            .map((id) => stories.find((story) => story.uuid === id))
            .filter(Boolean)
            .map((story) => ({
              ...story,
              href: currentLanguage === 'en' && story.translated_slugs?.[0]?.path
                ? `/en/${story.translated_slugs[0].path}`
                : `/${story.full_slug}`,
            }));

          // 2. SET THE FULL, UNFILTERED LIST in state.
          setAllStories(orderedStories);
        }
      })
      .catch(() => {});

    return () => { isMounted = false; };
  }, [tileIdsJoin]); // This dependency is correct. Only fetch when IDs change.

  // 3. Create your displayed 'tiles' list using useMemo.
  // This will re-calculate ONLY when 'isMobile' or 'allStories' changes.
  const tiles = React.useMemo(() => {
    if (isMobile) {
      const mobileTilesCount = blok.number_of_displayed_tiles_mobile
        ? Number(blok.number_of_displayed_tiles_mobile) : 0;
      return mobileTilesCount > 0 ? allStories.slice(0, mobileTilesCount) : allStories;
    }
    // If not mobile, just return the complete list
    return allStories;
  }, [isMobile, allStories, blok.number_of_displayed_tiles_mobile]);

  const styles = clsx(
    `${component} px-4 lg:px-16 grid gap-4 grid-cols-2 s:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 pb-8 lg:pb-32`,
  );

  const parentRoute = currentLanguage === 'en' ? '/en/references' : '/references';

  return (
    <div {...storyblokEditable(blok)} className={styles}>
      {/* 4. Your render logic now maps over the memoized 'tiles' list, which is always correct. */}
      {tiles.map((item) => (
        <div
          className="aspect-[16/9] relative h-full overflow-hidden"
          key={item.uuid}
        >
          {/* ... all your other JSX remains the same ... */}
          {item?.content?.image?.filename && (
            <ReferencesTileImage
              src={item.content.image.filename}
              className="w-auto object-cover h-full"
              alt={getStoryblokAlt(item?.content?.image, (currentLanguage === 'en' ? 'Reference link' : 'Referenzlink'))}
            />
          )}
          <Link
            to={item.href}
            className="absolute top-0 w-full h-full flex"
            aria-label={`${currentLanguage === 'en' ? 'Read about' : 'Lesen Sie mehr über'} ${item?.content?.title || 'Partner'}`}
          >
            <div className="absolute w-full h-full bg-green-800 z-10 opacity-75 hover:bg-slate-700 hover:opacity-20 transition" aria-hidden="true" />
            <div className="m-auto z-20 pointer-events-none">
              {item?.content?.logo?.filename ? (
                <img
                  src={item.content.logo.filename && !item.content.logo.filename.includes('.svg') ? `${item.content.logo.filename}/m/350x0/filters:format(webp)` : item.content.logo.filename}
                  width="175"
                  height="64"
                  className="max-h-8 s:max-h-12 xl:max-h-16 h-full max-w-[100px] s:max-w-[150px] xl:max-w-[175px] w-full object-contain"
                  alt={getStoryblokAlt(item?.content?.logo, (currentLanguage === 'en' ? 'Reference link' : 'Referenzlink'))}
                  loading="lazy"
                />
              ) : (
                <h4>{item.name || 'reference title'}</h4>
              )}
            </div>
          </Link>
        </div>
      ))}
      {blok?.showmore_tile && (
        <Link
          to={parentRoute}
          className="flex bg-green-800 hover:bg-green-900 text-white group aspect-[16/9]"
        >
          <div className="flex m-auto items-center group-hover:scale-105 transition-transform">
            <span className="mr-2">
              {currentLanguage === 'default' ? 'Mehr' : 'More'}
              <span className="sr-only">
                {currentLanguage === 'en' ? ' references' : ' Referenzen'}
              </span>
            </span>
            <FontAwesomeIcon
              icon={icon({ prefix: 'far', iconName: 'arrow-right' })}
              size="2x"
            />
          </div>
        </Link>
      )}
    </div>
  );
}
